import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { createBudget, getBudgets, deleteBudget, updateBudget } from '../services/budgetService';
import { getCategories } from '../services/categoryService';
import './Budgets.css';

const Budgets = () => {
  const { currentUser } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    period: 'mensual'
  });

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const loadData = async () => {
    if (currentUser) {
      const buds = await getBudgets(currentUser.uid);
      const cats = await getCategories(currentUser.uid);
      setBudgets(buds);
      setCategories(cats);

      // Establecer la primera categoría de egreso como default
      const firstEgreso = cats.find(c => c.type === 'egreso');
      if (firstEgreso && !formData.category) {
        setFormData(prev => ({ ...prev, category: firstEgreso.name }));
      }
    }
  };

  const loadBudgets = async () => {
    if (currentUser) {
      const buds = await getBudgets(currentUser.uid);
      setBudgets(buds);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingBudget) {
      await updateBudget(editingBudget.id, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
    } else {
      await createBudget(currentUser.uid, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
    }

    const firstCategory = egresoCategories[0];
    setFormData({
      category: firstCategory?.name || '',
      amount: '',
      period: 'mensual'
    });
    setShowForm(false);
    setEditingBudget(null);
    loadBudgets();
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({
      category: budget.category,
      amount: budget.amount.toString(),
      period: budget.period
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBudget(null);
    const firstCategory = egresoCategories[0];
    setFormData({
      category: firstCategory?.name || '',
      amount: '',
      period: 'mensual'
    });
  };

  const egresoCategories = categories.filter(c => c.type === 'egreso');

  const getTotalBudget = () => {
    return budgets.reduce((sum, budget) => sum + (budget.amount || 0), 0);
  };

  const getPeriodBreakdown = () => {
    const periods = {
      semanal: { total: 0, count: 0, period: 'Semanal' },
      mensual: { total: 0, count: 0, period: 'Mensual' },
      anual: { total: 0, count: 0, period: 'Anual' }
    };

    budgets.forEach(budget => {
      if (periods[budget.period]) {
        periods[budget.period].total += budget.amount || 0;
        periods[budget.period].count += 1;
      }
    });

    return Object.values(periods).filter(p => p.count > 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar este presupuesto?')) {
      await deleteBudget(id);
      loadBudgets();
    }
  };

  return (
    <div className="budgets-page">
      <header>
        <h1>📊 Presupuestos</h1>
        <button onClick={() => setShowForm(!showForm)} className="add-btn">
          {showForm ? 'Cancelar' : '+ Nuevo Presupuesto'}
        </button>
      </header>

      {showForm && (
        <form onSubmit={handleSubmit} className="budget-form">
          <h3>{editingBudget ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}</h3>

          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
          >
            {egresoCategories.length === 0 ? (
              <option value="">No hay categorías de egreso</option>
            ) : (
              egresoCategories.map(cat => (
                <option key={cat.id || cat.name} value={cat.name}>
                  {cat.icon} {cat.name}
                </option>
              ))
            )}
          </select>

          <input
            type="number"
            step="0.01"
            placeholder="Monto del presupuesto"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />

          <select
            value={formData.period}
            onChange={(e) => setFormData({ ...formData, period: e.target.value })}
          >
            <option value="semanal">Semanal</option>
            <option value="mensual">Mensual</option>
            <option value="anual">Anual</option>
          </select>

          <div className="form-buttons">
            <button type="submit" className="save-btn">
              {editingBudget ? 'Actualizar' : 'Guardar'}
            </button>
            <button type="button" onClick={handleCancel} className="cancel-btn">
              Cancelar
            </button>
          </div>
        </form>
      )}

      {budgets.length > 0 && (
        <div className="budget-summary">
          <div className="summary-card total-budget">
            <div className="summary-icon">💰</div>
            <div className="summary-info">
              <h3>Presupuesto Total</h3>
              <p className="summary-amount">${getTotalBudget().toFixed(2)}</p>
              <span className="summary-subtitle">{budgets.length} presupuesto{budgets.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="summary-card by-period">
            <div className="summary-icon">📅</div>
            <div className="summary-info">
              <h3>Por Período</h3>
              <div className="period-breakdown">
                {getPeriodBreakdown().map(item => (
                  <div key={item.period} className="period-item">
                    <span className="period-label">{item.period}:</span>
                    <span className="period-amount">${item.total.toFixed(2)}</span>
                    <span className="period-count">({item.count})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="budgets-list">
        {budgets.length === 0 ? (
          <div className="empty-state">
            <p>No hay presupuestos creados</p>
            <p className="hint">Crea tu primer presupuesto usando el botón "+ Nuevo Presupuesto"</p>
          </div>
        ) : (
          budgets.map(budget => (
            <div key={budget.id} className="budget-card">
              <div className="budget-header">
                <h3>{budget.category}</h3>
                <span className="period">{budget.period}</span>
              </div>
              <div className="budget-amount">
                <span className="label">Presupuesto:</span>
                <span className="amount">${budget.amount?.toFixed(2)}</span>
              </div>
              <div className="budget-actions">
                <button onClick={() => handleEdit(budget)} className="edit-btn">
                  ✏️ Editar
                </button>
                <button onClick={() => handleDelete(budget.id)} className="delete-btn">
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <a href="/dashboard" className="back-link">← Volver al Dashboard</a>
    </div>
  );
};

export default Budgets;
